import React, { useContext, useEffect, useRef, useState } from "react";

import { useParams, useHistory } from "react-router-dom";

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Grid,
  makeStyles,
  useTheme,
  Paper,
  Tab,
  Tabs,
  TextField,
  Typography,
  Box,
  Card,
  CardContent,
  CardActions,
  Chip,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
} from "@material-ui/core";
import {
  Add as AddIcon,
  Forum as ForumIcon,
  People as PeopleIcon,
  Message as MessageIcon,
  Info as InfoIcon,
  CheckCircle as CheckCircleIcon,
  Help as HelpIcon,
} from "@material-ui/icons";
import GuideOpenButton from "../../components/GuideOpenButton";
import ChatList from "./ChatList";
import ChatMessages from "./ChatMessages";
import { UsersFilter } from "../../components/UsersFilter";
import api from "../../services/api";
import { SocketContext } from "../../context/Socket/SocketContext";
import toastError from "../../errors/toastError";

import { has, isObject } from "lodash";

import { AuthContext } from "../../context/Auth/AuthContext";
import withWidth, { isWidthUp } from "@material-ui/core/withWidth";
import useMediaQuery from "@material-ui/core/useMediaQuery";

const useStyles = makeStyles((theme) => ({
  /** Base: sin overflow aquí; welcome vs. vista con chats en variantes */
  mainContainer: {
    display: "flex",
    flexDirection: "column",
    position: "relative",
    width: "100%",
    maxWidth: "100%",
    boxSizing: "border-box",
    padding: theme.spacing(2),
    border: "1px solid rgba(0, 0, 0, 0.12)",
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(1),
    },
  },
  /** Sin conversaciones: el propio Paper hace scroll (zoom / pantallas bajas) */
  mainContainerWelcome: {
    flex: "1 1 auto",
    minHeight: 0,
    maxHeight: "100%",
    overflowX: "hidden",
    overflowY: "auto",
    WebkitOverflowScrolling: "touch",
    alignSelf: "stretch",
  },
  /** Con lista + mensajes: altura fija al panel y scroll solo dentro de columnas */
  mainContainerWithChats: {
    flex: 1,
    minHeight: 0,
    overflow: "hidden",
  },
  /** Encadena flex desde layout main → Paper (altura limitada y scroll correcto) */
  chatPageOuter: {
    display: "flex",
    flexDirection: "column",
    flex: 1,
    minHeight: 0,
    width: "100%",
    maxWidth: "100%",
    maxHeight: "100%",
    alignSelf: "stretch",
  },
  gridContainer: {
    flex: 1,
    minHeight: 0,
    width: "100%",
    height: "100%",
    border: `1px solid ${theme.mode === "dark" ? "#334155" : "#e5e8f0"}`,
    backgroundColor: theme.mode === "dark" ? "#111827" : theme.palette.background.paper,
    borderRadius: 14,
  },
  tabLayoutRoot: {
    flex: 1,
    minHeight: 0,
    width: "100%",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },
  chatColumn: {
    display: "flex",
    flexDirection: "column",
    minHeight: 0,
    height: "100%",
  },
  messagesColumn: {
    display: "flex",
    flexDirection: "column",
    minHeight: 0,
    height: "100%",
  },
  gridItem: {
    height: "100%",
    minHeight: 0,
  },
  gridItemTab: {
    flex: 1,
    minHeight: 0,
    width: "100%",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },
  btnContainer: {
    textAlign: "right",
    padding: 10,
    flexShrink: 0,
    width: "100%",
    boxSizing: "border-box",
    [theme.breakpoints.down("sm")]: {
      textAlign: "center",
      padding: theme.spacing(1),
      "& .MuiButton-root": {
        width: "100%",
        maxWidth: "100%",
      },
    },
  },
  welcomeCardActions: {
    justifyContent: "center",
    flexWrap: "wrap",
    gap: theme.spacing(1),
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      alignItems: "stretch",
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(2),
    },
  },
  welcomePrimaryButton: {
    width: "100%",
    maxWidth: 420,
    [theme.breakpoints.up("sm")]: {
      width: "auto",
    },
  },
  welcomeRoot: {
    width: "100%",
    maxWidth: "100%",
    boxSizing: "border-box",
    minWidth: 0,
  },
  welcomeTitle: {
    wordBreak: "break-word",
    [theme.breakpoints.down("sm")]: {
      fontSize: "1.5rem",
    },
  },
  gettingStartedCard: {
    marginTop: theme.spacing(3),
  },
  gettingStartedTitle: {
    marginBottom: theme.spacing(1.5),
    wordBreak: "break-word",
  },
  gettingStartedList: {
    paddingTop: 0,
  },
  dialogActionsResponsive: {
    flexWrap: "wrap",
    gap: theme.spacing(1),
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      alignItems: "stretch",
      padding: theme.spacing(2),
    },
  },
  // ✅ NUEVOS ESTILOS PARA MEJOR UX
  welcomeCard: {
    marginBottom: theme.spacing(3),
    background: `linear-gradient(135deg, ${theme.palette.primary.main}15, ${theme.palette.secondary.main}15)`,
    border: `1px solid ${theme.palette.primary.main}30`,
  },
  welcomeHeader: {
    display: "flex",
    alignItems: "center",
    marginBottom: theme.spacing(2),
  },
  welcomeIcon: {
    marginRight: theme.spacing(1),
    color: theme.palette.primary.main,
  },
  featureCard: {
    marginBottom: theme.spacing(2),
    transition: "all 0.3s ease",
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: theme.shadows[4],
    },
  },
  featureIcon: {
    backgroundColor: theme.palette.primary.main,
    color: "white",
  },
  selectedUsers: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
  },
  // ✅ CORREGIDO: HelpSection dinámico según tema
  helpSection: {
    backgroundColor: theme.mode === 'dark' ? '#374151' : theme.palette.grey[50],
    padding: theme.spacing(2),
    borderRadius: 14,
    marginTop: theme.spacing(2),
    border: `1px solid ${theme.mode === 'dark' ? '#334155' : '#e5e8f0'}`,
  },
  // ✅ CORREGIDO: DialogTitle dinámico según tema (mantiene color primario)
  dialogTitle: {
    display: "flex",
    alignItems: "center",
    backgroundColor: theme.palette.primary.main,
    color: "white",
    padding: theme.spacing(2),
    margin: 0,
    borderRadius: "4px 4px 0 0",
    boxShadow: theme.mode === 'dark' ? '0 2px 8px rgba(0,0,0,0.3)' : '0 2px 8px rgba(0,0,0,0.1)',
    minWidth: 0,
    maxWidth: "100%",
    boxSizing: "border-box",
  },
  // ✅ CORREGIDO: DialogContent dinámico según tema
  dialogContent: {
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.primary,
    overflowX: "hidden",
    maxWidth: "100%",
    minWidth: 0,
    boxSizing: "border-box",
  },
  // ✅ CORREGIDO: SectionTitle dinámico según tema
  sectionTitle: {
    display: "flex",
    alignItems: "center",
    marginBottom: theme.spacing(2),
    color: theme.palette.primary.main,
  },
  // ✅ CORREGIDO: StepCard dinámico según tema
  stepCard: {
    marginBottom: theme.spacing(2),
    borderLeft: `4px solid ${theme.palette.primary.main}`,
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.mode === 'dark' ? '#4B5563' : '#e0e0e0'}`,
    boxShadow: theme.mode === 'dark' ? '0 2px 8px rgba(0,0,0,0.3)' : '0 2px 8px rgba(0,0,0,0.08)',
  },
  // ✅ CORREGIDO: TextField dinámico según tema
  textField: {
    '& .MuiOutlinedInput-root': {
      backgroundColor: theme.mode === 'dark' ? '#374151' : '#f5f5f5',
      color: theme.palette.text.primary,
      '&:hover': {
        backgroundColor: theme.mode === 'dark' ? '#4B5563' : '#eeeeee',
      },
      '&.Mui-focused': {
        backgroundColor: theme.mode === 'dark' ? '#374151' : 'white',
        boxShadow: `0 0 0 2px ${theme.mode === 'dark' ? 'rgba(59, 130, 246, 0.3)' : 'rgba(25, 118, 210, 0.2)'}`,
      },
      "& fieldset": {
        borderColor: theme.mode === 'dark' ? '#4B5563' : '#e0e0e0',
      },
      "&:hover fieldset": {
        borderColor: theme.palette.primary.main,
      },
      "&.Mui-focused fieldset": {
        borderColor: theme.palette.primary.main,
      },
    },
    "& .MuiInputLabel-root": {
      color: theme.palette.text.secondary || theme.palette.text.primary,
    },
    "& .MuiInputLabel-root.Mui-focused": {
      color: theme.palette.primary.main,
    },
    "& .MuiFormHelperText-root": {
      color: theme.palette.text.secondary || theme.palette.text.primary,
    },
  },
  // ✅ CORREGIDO: UserChip dinámico según tema
  userChip: {
    backgroundColor: theme.palette.primary.main,
    color: "white",
    border: `1px solid ${theme.palette.primary.main}`,
    '&:hover': {
      backgroundColor: theme.palette.primary.dark,
    },
  },
  // ✅ CORREGIDO: ListItem dinámico según tema
  listItem: {
    backgroundColor: theme.mode === 'dark' ? 'transparent' : 'transparent',
    '&:hover': {
      backgroundColor: theme.mode === 'dark' ? '#4B5563' : '#f5f5f5',
    },
  },
  // ✅ CORREGIDO: ListItemText dinámico según tema
  listItemText: {
    '& .MuiTypography-root': {
      color: theme.palette.text.primary,
    },
    '& .MuiTypography-body2': {
      color: theme.palette.text.secondary || theme.palette.text.primary,
    },
  },
}));

export function ChatModal({
  open,
  chat,
  type,
  handleClose,
  handleLoadNewChat,
}) {
  const classes = useStyles();
  const theme = useTheme();
  const isNarrow = useMediaQuery(theme.breakpoints.down("sm"));
  const [users, setUsers] = useState([]);
  const [title, setTitle] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    setTitle("");
    setUsers([]);
    setIsSaving(false);
    if (type === "edit" && chat) {
      const userList = (chat.users || [])
        .map((u) => ({
          id: u.user?.id ?? u.userId ?? u.id,
          name: u.user?.name ?? u.name ?? "",
        }))
        .filter((u) => u.id);
      setUsers(userList);
      setTitle(chat.title || "");
    }
  }, [chat, open, type]);

  const handleSave = async () => {
    // ✅ PREVENIR DOBLE CLIC
    if (isSaving) {
      console.log("⚠️ Guardado en progreso, ignorando clic duplicado");
      return;
    }

    try {
      if (!title.trim()) {
        alert("Por favor, ingresa un título para la conversación.");
        return;
      }

      if (!users || users.length === 0) {
        alert("Por favor, selecciona al menos un usuario para la conversación.");
        return;
      }

      setIsSaving(true);
      console.log("💾 Guardando conversación...", { title, users: users.length });

      if (type === "edit") {
        await api.put(`/chats/${chat.id}`, {
          users,
          title,
        });
        console.log("✅ Conversación actualizada correctamente");
      } else {
        const { data } = await api.post("/chats", {
          users,
          title,
        });
        console.log("✅ Conversación creada correctamente:", data.id);
        handleLoadNewChat(data);
      }
      handleClose();
    } catch (err) {
      console.error("❌ Error al guardar conversación:", err);
      alert("Error al guardar la conversación. Por favor, inténtalo de nuevo.");
    } finally {
      setIsSaving(false);
    }
  };

  const removeUser = (userId) => {
    setUsers(users.filter(user => user.id !== userId));
  };

  return (
      <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby="chat-dialog-title"
      aria-describedby="chat-dialog-description"
      maxWidth="md"
      fullWidth
      fullScreen={isNarrow}
      PaperProps={{
        style: {
          backgroundColor: theme.palette.background.paper,
          border: `1px solid ${theme.mode === 'dark' ? '#4B5563' : '#e0e0e0'}`,
          overflowX: "hidden",
          maxWidth: "100%",
        },
      }}
    >
      <div className={classes.dialogTitle}>
        <ForumIcon style={{ marginRight: 8 }} />
        <Typography variant="h6">
          {type === "edit" ? "Editar Conversación" : "Nueva Conversación"}
        </Typography>
      </div>
      
      <DialogContent className={classes.dialogContent}>
        <Grid container spacing={2} style={{ margin: 0, width: "100%" }}>
          {/* ✅ SECCIÓN 1: INFORMACIÓN BÁSICA */}
          <Grid item xs={12}>
            <div className={classes.sectionTitle}>
              <InfoIcon style={{ marginRight: 8 }} />
              <Typography variant="h6">Información Básica</Typography>
            </div>
            
            <Card className={classes.stepCard} style={{ overflow: "hidden", maxWidth: "100%" }}>
              <CardContent style={{ backgroundColor: theme.palette.background.paper }}>
                <TextField
                  label="Título de la conversación"
                  placeholder="Ej: Reunión de equipo, Proyecto XYZ, etc."
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  variant="outlined"
                  fullWidth
                  helperText="Ingresa un título descriptivo para identificar fácilmente esta conversación"
                  className={classes.textField}
                />
              </CardContent>
            </Card>
          </Grid>

          {/* ✅ SECCIÓN 2: SELECCIÓN DE USUARIOS */}
          <Grid item xs={12}>
            <div className={classes.sectionTitle}>
              <PeopleIcon style={{ marginRight: 8 }} />
              <Typography variant="h6">Participantes</Typography>
            </div>
            
            <Card className={classes.stepCard} style={{ overflow: "hidden", maxWidth: "100%" }}>
              <CardContent style={{ backgroundColor: theme.palette.background.paper }}>
                <Typography variant="body2" style={{ color: theme.palette.text.secondary || theme.palette.text.primary }} gutterBottom>
                  Selecciona los usuarios que participarán en esta conversación:
                </Typography>
                
                <UsersFilter
                  onFiltered={(users) => setUsers(users)}
                  initialUsers={users}
                />
                
                {/* ✅ MOSTRAR USUARIOS SELECCIONADOS */}
                {users.length > 0 && (
                  <Box mt={2}>
                    <Typography variant="subtitle2" style={{ color: theme.palette.text.primary }} gutterBottom>
                      Usuarios seleccionados ({users.length}):
                    </Typography>
                    <div className={classes.selectedUsers}>
                      {users.map((user) => (
                        <Chip
                          key={user.id}
                          label={user.name}
                          onDelete={() => removeUser(user.id)}
                          className={classes.userChip}
                          avatar={<Avatar>{user.name.charAt(0).toUpperCase()}</Avatar>}
                        />
                      ))}
                    </div>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* ✅ SECCIÓN 3: AYUDA CONTEXTUAL */}
          <Grid item xs={12}>
            <div className={classes.helpSection}>
              <div className={classes.sectionTitle}>
                <HelpIcon style={{ marginRight: 8 }} />
                <Typography variant="h6">¿Cómo funciona?</Typography>
              </div>
              
              <List dense>
                <ListItem className={classes.listItem}>
                  <ListItemAvatar>
                    <Avatar className={classes.featureIcon}>
                      <CheckCircleIcon />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText 
                    className={classes.listItemText}
                    primary="Comunicación interna"
                    secondary="Este chat es privado y solo visible para los usuarios seleccionados"
                  />
                </ListItem>
                
                <ListItem className={classes.listItem}>
                  <ListItemAvatar>
                    <Avatar className={classes.featureIcon}>
                      <MessageIcon />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText 
                    className={classes.listItemText}
                    primary="Mensajes en tiempo real"
                    secondary="Los mensajes se sincronizan automáticamente entre todos los participantes"
                  />
                </ListItem>
                
                <ListItem className={classes.listItem}>
                  <ListItemAvatar>
                    <Avatar className={classes.featureIcon}>
                      <PeopleIcon />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText 
                    className={classes.listItemText}
                    primary="Colaboración eficiente"
                    secondary="Ideal para coordinación de equipos, proyectos y comunicación interna"
                  />
                </ListItem>
              </List>
            </div>
          </Grid>
        </Grid>
      </DialogContent>
      
      <DialogActions
        className={classes.dialogActionsResponsive}
        style={{
          backgroundColor: theme.palette.background.paper,
          borderTop: `1px solid ${theme.mode === 'dark' ? '#4B5563' : '#e0e0e0'}`,
        }}
      >
        <Button
          onClick={handleClose}
          color="secondary"
          fullWidth={isNarrow}
          style={{ color: theme.palette.error.main }}
        >
          Cancelar
        </Button>
        <Button
          onClick={handleSave}
          color="primary"
          variant="contained"
          startIcon={<AddIcon />}
          fullWidth={isNarrow}
          disabled={!title.trim() || users.length === 0 || isSaving}
        >
          {isSaving ? "Guardando..." : (type === "edit" ? "Actualizar" : "Crear Conversación")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function Chat(props) {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const history = useHistory();

  const [showDialog, setShowDialog] = useState(false);
  const [dialogType, setDialogType] = useState("new");
  const [currentChat, setCurrentChat] = useState({});
  const [chats, setChats] = useState([]);
  const [chatsPageInfo, setChatsPageInfo] = useState({ hasMore: false });
  const [messages, setMessages] = useState([]);
  const [messagesPageInfo, setMessagesPageInfo] = useState({ hasMore: false });
  const [messagesPage, setMessagesPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState(0);
  const isMounted = useRef(true);
  const scrollToBottomRef = useRef();
  const { id } = useParams();

  const socketManager = useContext(SocketContext);

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (isMounted.current) {
      findChats().then((data) => {
        const { records } = data;
        if (records.length > 0) {
          setChats(records);
          setChatsPageInfo(data);

          if (id && records.length) {
            const chat = records.find((r) => r.uuid === id);
            selectChat(chat);
          }
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (id) return;

    setCurrentChat({});
    setMessages([]);
    setMessagesPage(1);
    setMessagesPageInfo({ hasMore: false });
    setTab(0);
  }, [id]);

  useEffect(() => {
    if (isObject(currentChat) && has(currentChat, "id")) {
      findMessages(currentChat.id).then(() => {
        if (typeof scrollToBottomRef.current === "function") {
          setTimeout(() => {
            scrollToBottomRef.current();
          }, 300);
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentChat?.id]);

  useEffect(() => {
    const companyId = localStorage.getItem("companyId");
    const socket = socketManager.getSocket(companyId);

    const chatUserEvent = `company-${companyId}-chat-user-${user.id}`;
    const companyChatEvent = `company-${companyId}-chat`;
    const currentChatEvent = isObject(currentChat) && has(currentChat, "id")
      ? `company-${companyId}-chat-${currentChat.id}`
      : null;

    const handleUserChat = (data) => {
      if (data.action === "create") {
        setChats((prev) => {
          const exists = prev.some(chat => chat.id === data.record.id);
          return exists ? prev : [data.record, ...prev];
        });
      }
      if (data.action === "update") {
        setChats((prev) => prev.map((chat) => {
          if (chat.id === data.record.id) return { ...data.record };
          return chat;
        }));
        setCurrentChat((prev) => {
          if (prev?.id === data.record.id) {
            const next = data.record;
            const nextUsers = Array.isArray(next.users) ? next.users : [];
            const prevUsers = Array.isArray(prev?.users) ? prev.users : [];
            const nestedOk = nextUsers.some((u) => u?.user?.id);
            if (!nestedOk && prevUsers.some((u) => u?.user?.id)) {
              return { ...next, users: prevUsers };
            }
            return next;
          }
          return prev;
        });
      }
    };

    const handleCompanyChat = (data) => {
      if (data.action === "create") {
        setChats((prev) => {
          const exists = prev.some(chat => chat.id === data.record.id);
          return exists ? prev : [data.record, ...prev];
        });
      }
      if (data.action === "delete") {
        setChats((prev) => prev.filter((c) => c.id !== +data.id));
        setCurrentChat((prev) => {
          if (prev?.id === +data.id) {
            setMessages([]);
            setMessagesPage(1);
            setMessagesPageInfo({ hasMore: false });
            setTab(0);
            history.push("/chats");
            return {};
          }
          return prev;
        });
      }
      if (data.action === "new-message" && data.chat?.id) {
        setChats((prev) => prev.map((chat) => {
          if (chat.id === data.chat.id) return { ...data.chat };
          return chat;
        }));
      }
      if (data.action === "update" && data.chat?.id) {
        setChats((prev) => prev.map((chat) => {
          if (chat.id === data.chat.id) return { ...data.chat };
          return chat;
        }));
        setCurrentChat((prev) => {
          if (prev?.id === data.chat.id) {
            const next = data.chat;
            const nextUsers = Array.isArray(next.users) ? next.users : [];
            const prevUsers = Array.isArray(prev?.users) ? prev.users : [];
            const nestedOk = nextUsers.some((u) => u?.user?.id);
            if (!nestedOk && prevUsers.some((u) => u?.user?.id)) {
              return { ...next, users: prevUsers };
            }
            return next;
          }
          return prev;
        });
      }
    };

    const handleCurrentChat = (data) => {
      if (data.action === "new-message") {
        setMessages((prev) => {
          const exists = prev.some((message) => message.id === data.newMessage.id);
          return exists ? prev : [...prev, data.newMessage];
        });
        setChats((prev) => prev.map((chat) => {
          if (chat.id === data.newMessage.chatId) return { ...data.chat };
          return chat;
        }));
        if (typeof scrollToBottomRef.current === "function") {
          scrollToBottomRef.current();
        }
      }

      if (data.action === "update") {
        setChats((prev) => prev.map((chat) => {
          if (chat.id === data.chat.id) return { ...data.chat };
          return chat;
        }));
        setCurrentChat((prev) => {
          if (prev?.id === data.chat.id) {
            const next = data.chat;
            const nextUsers = Array.isArray(next.users) ? next.users : [];
            const prevUsers = Array.isArray(prev?.users) ? prev.users : [];
            const nestedOk = nextUsers.some((u) => u?.user?.id);
            if (!nestedOk && prevUsers.some((u) => u?.user?.id)) {
              return { ...next, users: prevUsers };
            }
            return next;
          }
          return prev;
        });
        if (typeof scrollToBottomRef.current === "function") {
          scrollToBottomRef.current();
        }
      }
    };

    socket.on(chatUserEvent, handleUserChat);
    socket.on(companyChatEvent, handleCompanyChat);
    if (currentChatEvent) {
      socket.on(currentChatEvent, handleCurrentChat);
    }

    return () => {
      if (socket && typeof socket.off === 'function') {
        socket.off(chatUserEvent, handleUserChat);
        socket.off(companyChatEvent, handleCompanyChat);
        if (currentChatEvent) {
          socket.off(currentChatEvent, handleCurrentChat);
        }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentChat?.id, socketManager, user.id]);

  const selectChat = (chat) => {
    if (!chat?.id) {
      setCurrentChat({});
      setMessages([]);
      setMessagesPage(1);
      setMessagesPageInfo({ hasMore: false });
      setTab(0);
      history.push("/chats");
      return;
    }
    try {
      setMessages([]);
      setMessagesPage(1);
      setCurrentChat(chat);
      setTab(1);
    } catch (err) {}
  };

  const sendMessage = async (contentMessage) => {
    if (!currentChat?.id || !contentMessage?.trim() || loading) return false;
    setLoading(true);
    try {
      await api.post(`/chats/${currentChat.id}/messages`, {
        message: contentMessage,
      });
      return true;
    } catch (err) {
      toastError(err);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const deleteChat = async (chat) => {
    try {
      await api.delete(`/chats/${chat.id}`);
      
      // ✅ ACTUALIZAR LISTA DE CHATS DESPUÉS DE ELIMINAR
      const updatedChats = chats.filter(c => c.id !== chat.id);
      setChats(updatedChats);
      
      // ✅ SI LA CONVERSACIÓN ELIMINADA ERA LA ACTUAL, REDIRIGIR
      if (currentChat.id === chat.id) {
        setCurrentChat({});
        setMessages([]);
        setMessagesPage(1);
        setMessagesPageInfo({ hasMore: false });
        
        // ✅ REDIRIGIR A LISTA DE CONVERSACIONES SI HAY OTRAS, SINO A PÁGINA PRINCIPAL
        if (updatedChats.length > 0) {
          // Hay otras conversaciones, quedarse en la lista
          setTab(0); // Cambiar a pestaña de lista
          history.push("/chats");
        } else {
          // No hay más conversaciones, ir a página principal
          setTab(0);
          history.push("/chats");
        }
      }
      
      console.log("✅ Conversación eliminada correctamente:", chat.title);
    } catch (err) {
      console.error("❌ Error al eliminar conversación:", err);
    }
  };

  const findMessages = async (chatId) => {
    setLoading(true);
    try {
      const { data } = await api.get(
        `/chats/${chatId}/messages?pageNumber=${messagesPage}`
      );
      setMessagesPage((prev) => prev + 1);
      setMessagesPageInfo(data);
      setMessages((prev) => [...data.records, ...prev]);
    } catch (err) {}
    setLoading(false);
  };

  const loadMoreMessages = async () => {
    if (!loading) {
      findMessages(currentChat.id);
    }
  };

  const findChats = async () => {
    try {
      const { data } = await api.get("/chats");
      return data;
    } catch (err) {
      console.log(err);
      return { records: [], count: 0, hasMore: false };
    }
  };

  // ✅ COMPONENTE DE BIENVENIDA PARA MEJOR UX
  const renderWelcomeScreen = () => {
    return (
      <Box className={classes.welcomeRoot} p={{ xs: 1.5, sm: 2, md: 3 }}>
        {/* ✅ TARJETA DE BIENVENIDA */}
        <Card className={classes.welcomeCard}>
          <CardContent>
            <div className={classes.welcomeHeader}>
              <ForumIcon className={classes.welcomeIcon} />
              <Typography variant="h4" component="h1" className={classes.welcomeTitle}>
                Chat Interno
              </Typography>
              <GuideOpenButton id="chat" title="Chat Interno" to="/chats" startId="home" />
            </div>
            
            <Typography variant="h6" color="textSecondary" paragraph>
              Sistema de comunicación interna para equipos y colaboración
            </Typography>
            
            <Typography variant="body1" paragraph>
              El Chat Interno te permite comunicarte de forma privada con otros miembros de tu equipo, 
              compartir información importante y coordinar proyectos de manera eficiente.
            </Typography>
          </CardContent>
        </Card>

        {/* ✅ CARACTERÍSTICAS PRINCIPALES — 1 col móvil/zoom, 2 tablet, 3 desktop */}
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} lg={4}>
            <Card className={classes.featureCard}>
              <CardContent>
                <Avatar className={classes.featureIcon}>
                  <PeopleIcon />
                </Avatar>
                <Typography variant="h6" gutterBottom>
                  Comunicación Privada
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Chats privados solo visibles para los participantes seleccionados
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} lg={4}>
            <Card className={classes.featureCard}>
              <CardContent>
                <Avatar className={classes.featureIcon}>
                  <MessageIcon />
                </Avatar>
                <Typography variant="h6" gutterBottom>
                  Tiempo Real
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Mensajes sincronizados instantáneamente entre todos los participantes
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} lg={4}>
            <Card className={classes.featureCard}>
              <CardContent>
                <Avatar className={classes.featureIcon}>
                  <CheckCircleIcon />
                </Avatar>
                <Typography variant="h6" gutterBottom>
                  Colaboración Eficiente
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Ideal para coordinación de equipos, proyectos y comunicación interna
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* ✅ PASOS PARA COMENZAR */}
        <Card className={classes.gettingStartedCard}>
          <CardContent>
            <Typography variant="h6" className={classes.gettingStartedTitle}>
              ¿Cómo comenzar?
            </Typography>
            
            <List className={classes.gettingStartedList}>
              <ListItem>
                <ListItemAvatar>
                  <Avatar style={{ backgroundColor: '#4CAF50' }}>1</Avatar>
                </ListItemAvatar>
                <ListItemText 
                  primary="Haz clic en 'Nueva Conversación'"
                  secondary="Crea tu primera conversación interna"
                />
              </ListItem>
              
              <ListItem>
                <ListItemAvatar>
                  <Avatar style={{ backgroundColor: '#2196F3' }}>2</Avatar>
                </ListItemAvatar>
                <ListItemText 
                  primary="Selecciona los participantes"
                  secondary="Elige los usuarios que participarán en la conversación"
                />
              </ListItem>
              
              <ListItem>
                <ListItemAvatar>
                  <Avatar style={{ backgroundColor: '#FF9800' }}>3</Avatar>
                </ListItemAvatar>
                <ListItemText 
                  primary="¡Comienza a chatear!"
                  secondary="Envía mensajes y mantén a tu equipo comunicado"
                />
              </ListItem>
            </List>
          </CardContent>
          
          <CardActions className={classes.welcomeCardActions}>
            <Button
              onClick={() => {
                setDialogType("new");
                setShowDialog(true);
              }}
              color="primary"
              variant="contained"
              size="large"
              startIcon={<AddIcon />}
              className={classes.welcomePrimaryButton}
            >
              Crear Primera Conversación
            </Button>
          </CardActions>
        </Card>
      </Box>
    );
  };

  const renderGrid = () => {
    // ✅ MOSTRAR PANTALLA DE BIENVENIDA SI NO HAY CONVERSACIONES
    if (chats.length === 0 && !loading) {
      return renderWelcomeScreen();
    }

    return (
      <Grid className={classes.gridContainer} container>
        <Grid className={`${classes.gridItem} ${classes.chatColumn}`} item xs={12} md={3}>
          <div className={classes.btnContainer}>
            <Button
              onClick={() => {
                setDialogType("new");
                setShowDialog(true);
              }}
              color="primary"
              variant="contained"
              startIcon={<AddIcon />}
              size="large"
              style={{
                borderRadius: 8,
                textTransform: 'none',
                fontWeight: 600,
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 6px 16px rgba(0,0,0,0.2)',
                }
              }}
            >
              Nueva Conversación
            </Button>
            <GuideOpenButton id="chat" title="Chat Interno" to="/chats" startId="home" />
          </div>

          <ChatList
            chats={chats}
            pageInfo={chatsPageInfo}
            loading={loading}
            handleSelectChat={(chat) => selectChat(chat)}
            handleDeleteChat={(chat) => deleteChat(chat)}
            handleEditChat={(chat) => {
              selectChat(chat);
              setDialogType("edit");
              setShowDialog(true);
            }}
          />
        </Grid>
        <Grid className={`${classes.gridItem} ${classes.messagesColumn}`} item xs={12} md={9}>
          {isObject(currentChat) && has(currentChat, "id") ? (
            <ChatMessages
              chat={currentChat}
              scrollToBottomRef={scrollToBottomRef}
              pageInfo={messagesPageInfo}
              messages={messages}
              loading={loading}
              handleSendMessage={sendMessage}
              handleLoadMore={loadMoreMessages}
            />
          ) : (
            <Box 
              display="flex" 
              flexDirection="column" 
              alignItems="center" 
              justifyContent="center" 
              height="100%"
              p={3}
            >
              <ForumIcon style={{ fontSize: 64, color: '#ccc', marginBottom: 16 }} />
              <Typography variant="h6" color="textSecondary" gutterBottom>
                Selecciona una conversación
              </Typography>
              <Typography variant="body2" color="textSecondary" align="center">
                Elige una conversación de la lista para comenzar a chatear, o crea una nueva conversación
              </Typography>
            </Box>
          )}
        </Grid>
      </Grid>
    );
  };

  const renderTab = () => {
    // ✅ MOSTRAR PANTALLA DE BIENVENIDA SI NO HAY CONVERSACIONES EN MÓVIL
    if (chats.length === 0 && !loading) {
      return renderWelcomeScreen();
    }

    return (
      <Grid className={`${classes.gridContainer} ${classes.tabLayoutRoot}`} container direction="column">
        <Grid item xs={12} style={{ flexShrink: 0 }}>
          <Tabs
            value={tab}
            indicatorColor="primary"
            textColor="primary"
            onChange={(e, v) => setTab(v)}
            aria-label="chat tabs"
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab 
              label="Conversaciones" 
              icon={<ForumIcon />}
            />
            <Tab 
              label="Mensajes" 
              icon={<MessageIcon />}
              disabled={!isObject(currentChat) || !has(currentChat, "id")}
            />
          </Tabs>
        </Grid>
        
        <Grid className={classes.gridItemTab} item xs={12} style={{ display: tab === 0 ? undefined : "none" }}>
            <div className={classes.btnContainer}>
              <Button
                onClick={() => {
                  setDialogType("new");
                  setShowDialog(true);
                }}
                color="primary"
                variant="contained"
                startIcon={<AddIcon />}
                fullWidth
                size="large"
                style={{
                  borderRadius: 8,
                  textTransform: 'none',
                  fontWeight: 600,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 6px 16px rgba(0,0,0,0.2)',
                  }
                }}
              >
                Nueva Conversación
              </Button>
              <GuideOpenButton id="chat" title="Chat Interno" to="/chats" startId="home" />
            </div>
            <ChatList
              chats={chats}
              pageInfo={chatsPageInfo}
              loading={loading}
              handleSelectChat={(chat) => selectChat(chat)}
              handleDeleteChat={(chat) => deleteChat(chat)}
              handleEditChat={(chat) => {
                selectChat(chat);
                setDialogType("edit");
                setShowDialog(true);
              }}
            />
        </Grid>
        
        <Grid className={classes.gridItemTab} item xs={12} style={{ display: tab === 1 ? undefined : "none" }}>
            {isObject(currentChat) && has(currentChat, "id") ? (
              <ChatMessages
                chat={currentChat}
                scrollToBottomRef={scrollToBottomRef}
                pageInfo={messagesPageInfo}
                messages={messages}
                loading={loading}
                handleSendMessage={sendMessage}
                handleLoadMore={loadMoreMessages}
              />
            ) : (
              <Box 
                display="flex" 
                flexDirection="column" 
                alignItems="center" 
                justifyContent="center" 
                height="100%"
                p={3}
              >
                <MessageIcon style={{ fontSize: 64, color: '#ccc', marginBottom: 16 }} />
                <Typography variant="h6" color="textSecondary" gutterBottom>
                  Selecciona una conversación
                </Typography>
                <Typography variant="body2" color="textSecondary" align="center">
                  Ve a la pestaña "Conversaciones" y elige una conversación para comenzar a chatear
                </Typography>
              </Box>
            )}
        </Grid>
      </Grid>
    );
  };

  /** Mientras no hay chats (bienvenida o carga): Paper con scroll; con lista fija altura a columnas */
  const useScrollableChatPanel = chats.length === 0;

  return (
    <>
      <ChatModal
        type={dialogType}
        open={showDialog}
        chat={currentChat}
        handleLoadNewChat={(data) => {
          console.log("🔄 handleLoadNewChat ejecutado:", data.id);
          setMessages([]);
          setMessagesPage(1);
          setCurrentChat(data);
          setTab(1);
          history.push(`/chats/${data.uuid}`);
          
          // ✅ REFRESCAR LISTA DE CHATS DESPUÉS DE CREAR NUEVA CONVERSACIÓN
          findChats().then((chatData) => {
            const { records } = chatData;
            if (records.length > 0) {
              setChats(records);
              setChatsPageInfo(chatData);
              console.log("✅ Lista de chats actualizada después de crear nueva conversación");
            }
          }).catch(err => {
            console.error("❌ Error al refrescar lista de chats:", err);
          });
        }}
        handleClose={() => setShowDialog(false)}
      />
      <Box className={classes.chatPageOuter}>
        <Paper
          className={`${classes.mainContainer} ${
            useScrollableChatPanel ? classes.mainContainerWelcome : classes.mainContainerWithChats
          }`}
        >
          {isWidthUp("lg", props.width) ? renderGrid() : renderTab()}
        </Paper>
      </Box>
    </>
  );
}

export default withWidth()(Chat);
